import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Pause, CheckCircle2, Music, ArrowRight, ArrowLeft, Volume2
} from 'lucide-react';
import axios from '../api/axios';
import './MusicSurvey.css';

// ─── Config ────────────────────────────────────────────────────────────────────
const CLIPS_BASE = [
  { id: 'happy',   src: '/audio/happy.mp3', mood: 'happy'   },
  { id: 'sad',     src: '/audio/sad.mp3',   mood: 'sad'     },
  { id: 'neutral', src: null,               mood: 'neutral' },
];

// Fisher-Yates shuffle — guaranteed unique order every call
function shuffleClips() {
  const arr = [...CLIPS_BASE];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const STAI_ITEMS = [
  { id: 'calm',    label: 'I feel calm'    },
  { id: 'tense',   label: 'I feel tense'   },
  { id: 'upset',   label: 'I feel upset'   },
  { id: 'relaxed', label: 'I feel relaxed' },
  { id: 'content', label: 'I feel at ease' },
  { id: 'worried', label: 'I feel worried' },
];

const RATING_ITEMS = [
  { id: 'enjoyment',   label: 'How much did you enjoy this piece?',        left: 'Not at all',     right: 'Very much'    },
  { id: 'emotional',   label: 'How emotionally moving was it?',             left: 'Not at all',     right: 'Very much'    },
  { id: 'calming',     label: 'How calming did you find it?',               left: 'Not at all',     right: 'Very calming' },
  { id: 'familiarity', label: 'How familiar is this type of music to you?', left: 'Completely new', right: 'Very familiar' },
];

// PSS-10 (Cohen, 1983). Positive items (reverse-scored on server): q4, q5, q7, q8
const PSS_ITEMS = [
  { id: 'q1',  label: 'been upset because of something that happened unexpectedly?' },
  { id: 'q2',  label: 'felt that you were unable to control the important things in your life?' },
  { id: 'q3',  label: 'felt nervous and stressed?' },
  { id: 'q4',  label: 'felt confident about your ability to handle your personal problems?' },
  { id: 'q5',  label: 'felt that things were going your way?' },
  { id: 'q6',  label: 'found that you could not cope with all the things that you had to do?' },
  { id: 'q7',  label: 'been able to control irritations in your life?' },
  { id: 'q8',  label: 'felt that you were on top of things?' },
  { id: 'q9',  label: 'been angered because of things that were outside of your control?' },
  { id: 'q10', label: 'felt that difficulties were piling up so high that you could not overcome them?' },
];
const PSS_OPTIONS = ['Never', 'Almost\nNever', 'Sometimes', 'Fairly\nOften', 'Very\nOften'];

const SEG_LABELS = ['Not at all', 'Somewhat', 'Moderately', 'Very much so'];

const REST_SECONDS = 30;

// ─── Screen builder ────────────────────────────────────────────────────────────
function buildScreens(clipsArray) {
  const sc = [
    { id: 'permission'            },
    { id: 'demographics_conceive' },
    { id: 'pss10'                 },
  ];
  for (let i = 0; i < clipsArray.length; i++) {
    const prevIsNeutral = i > 0 && clipsArray[i - 1].mood === 'neutral';
    if (!prevIsNeutral) sc.push({ id: 'pre_stai', clipIdx: i });

    if (clipsArray[i].mood === 'neutral') {
      sc.push({ id: 'rest_stimulus', clipIdx: i });
    } else {
      sc.push({ id: 'listen',      clipIdx: i });
      sc.push({ id: 'clip_rating', clipIdx: i });
    }

    sc.push({ id: 'clip_stai', clipIdx: i });
    if (i < clipsArray.length - 1 && clipsArray[i].mood !== 'neutral') {
      sc.push({ id: 'rest_inter', clipIdx: i });
    }
  }
  sc.push({ id: 'complete' });
  return sc;
}

const PHASE_LABEL = {
  permission:            'Consent',
  demographics_conceive: 'About you',
  pss10:                 'Stress questionnaire',
  pre_stai:              'Before listening',
  rest_stimulus:         'Rest period',
  listen:                'Listening',
  clip_rating:           'Rate the piece',
  clip_stai:             'After piece',
  rest_inter:            'Rest period',
  complete:              'Done',
};

// ─── Lock screen ───────────────────────────────────────────────────────────────
function LockScreen({ onUnlock }) {
  const [input,   setInput]   = useState('');
  const [error,   setError]   = useState(false);
  const [loading, setLoading] = useState(false);

  const attempt = async () => {
    if (!input) return;
    setLoading(true);
    try {
      await axios.post('/survey/verify-passcode', { passcode: input });
      localStorage.setItem('surveyUnlocked', 'true');
      onUnlock();
    } catch {
      setError(true);
      setInput('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-shell">
      <div className="survey-inner lock-screen">
        <h1 className="h1">Enter Passcode</h1>
        <input type="password" value={input} className="lock-input" placeholder="Passcode"
          onChange={e => { setInput(e.target.value); setError(false); }}
          onKeyDown={e => e.key === 'Enter' && attempt()} />
        {error && <p className="error-msg">Incorrect passcode. Try again.</p>}
        <button className="next-btn" onClick={attempt} disabled={loading}>
          {loading ? 'Verifying...' : 'Enter'}
        </button>
      </div>
    </div>
  );
}

// ─── Blank state helpers ────────────────────────────────────────────────────────
const blankStai   = () => ({ calm: null, tense: null, upset: null, relaxed: null, content: null, worried: null });
const blankRating = () => ({ enjoyment: 50, emotional: 50, calming: 50, familiarity: 50 });
const blankPss10  = () => Object.fromEntries(PSS_ITEMS.map(it => [it.id, null]));

// ─── Sub-components ────────────────────────────────────────────────────────────
const Chip = ({ label, selected, onClick, hasError }) => (
  <button type="button" onClick={onClick}
    className={`chip${selected ? ' chip--selected' : ''}${hasError ? ' chip--error' : ''}`}>
    {label}
  </button>
);

const RatingItem = ({ label, left, right, val, id, onChange }) => (
  <div className="vas-item">
    <div className="vas-label">{label}</div>
    <input type="range" min="0" max="100" step="any" value={val}
      onChange={e => onChange(id, +e.target.value)} className="vas-range" />
    <div className="vas-ends"><span>{left}</span><span>{right}</span></div>
    <div className="vas-value">{Math.round(val)}</div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────
export default function MusicSurvey() {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('surveyUnlocked') === 'true');

  // Clip order re-shuffles when localStorage key is absent (cleared after each submission)
  const [CLIPS]   = useState(() => { const s = localStorage.getItem('survey_clips'); return s ? JSON.parse(s) : shuffleClips(); });
  const [SCREENS] = useState(() => buildScreens(CLIPS));

  const topRef   = useRef(null);
  const audioRef = useRef(null);

  const [screenIdx, setScreenIdx] = useState(() => {
    const s = localStorage.getItem('survey_screenIdx');
    return s ? parseInt(s, 10) : 0;
  });

  // Set after the PSS-10 initial save; allows final PATCH to the same document
  const [surveyId, setSurveyId] = useState(() => localStorage.getItem('survey_id') || null);

  const [restCount,  setRestCount]  = useState(REST_SECONDS);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [isPlaying,     setIsPlaying]     = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioTime,     setAudioTime]     = useState('0:00');
  const [volume,        setVolume]        = useState(1);

  const [hasListenedMinimum, setHasListenedMinimum] = useState(() =>
    localStorage.getItem('survey_hasListenedMinimum') === 'true'
  );

  const [answers, setAnswers] = useState(() => {
    const s = localStorage.getItem('survey_answers');
    return s ? JSON.parse(s) : {
      permissionGiven: false,
      personalInfo: {
        name: '', email: '', phone: '', age: '', sex: '',
        maritalStatus: '', durationOfMarriage: '', numberOfChildren: '',
        ageOfYoungestChild: '', working: '', education: '', location: '',
      },
      conceiveInfo: {
        tryingToConceive: '', howLong: '', treatment: '', treatmentType: '',
      },
      pss10: blankPss10(),
      blocks: [0, 1, 2].map(() => ({
        preStai: blankStai(), postStai: blankStai(), clipRating: blankRating(),
      })),
    };
  });

  // ─── Autosave — clear on complete so next participant gets a fresh shuffle ─────
  useEffect(() => {
    if (screenIdx === SCREENS.length - 1) {
      ['survey_clips','survey_screenIdx','survey_answers','survey_hasListenedMinimum','survey_id']
        .forEach(k => localStorage.removeItem(k));
    } else {
      localStorage.setItem('survey_clips',              JSON.stringify(CLIPS));
      localStorage.setItem('survey_screenIdx',          screenIdx.toString());
      localStorage.setItem('survey_answers',            JSON.stringify(answers));
      localStorage.setItem('survey_hasListenedMinimum', hasListenedMinimum.toString());
      if (surveyId) localStorage.setItem('survey_id', surveyId);
    }
  }, [CLIPS, screenIdx, answers, hasListenedMinimum, surveyId, SCREENS.length]);

  // ─── Field updaters ────────────────────────────────────────────────────────────
  const updatePI  = (f, v) => { setAnswers(p => ({ ...p, personalInfo: { ...p.personalInfo, [f]: v } })); setErrors(p => { const n = { ...p }; delete n[f]; return n; }); };
  const updateCI  = (f, v) => { setAnswers(p => ({ ...p, conceiveInfo: { ...p.conceiveInfo, [f]: v } })); setErrors(p => { const n = { ...p }; delete n[f]; return n; }); };
  const updatePss = (f, v) => { setAnswers(p => ({ ...p, pss10: { ...p.pss10, [f]: v } }));              setErrors(p => { const n = { ...p }; delete n[f]; return n; }); };

  const cur      = SCREENS[screenIdx];
  const totalPct = Math.round((screenIdx / (SCREENS.length - 1)) * 100);

  // ─── Effects ───────────────────────────────────────────────────────────────────
  useEffect(() => { topRef.current?.scrollIntoView({ behavior: 'instant' }); }, [screenIdx]);
  useEffect(() => { if (cur.id === 'listen') setHasListenedMinimum(false); }, [cur.id, cur.clipIdx]);
  useEffect(() => setErrors({}), [screenIdx]);

  useEffect(() => {
    if (!cur.id.startsWith('rest')) return;
    const initial = cur.id === 'rest_stimulus' ? 30 : 15;
    setRestCount(initial);
    const t = setInterval(() => setRestCount(p => { if (p <= 1) { clearInterval(t); return 0; } return p - 1; }), 1000);
    return () => clearInterval(t);
  }, [cur]);

  useEffect(() => {
    if (cur.id !== 'listen') return;
    setIsPlaying(false); setAudioProgress(0); setAudioTime('0:00');
    let objectUrl = null;
    const src = CLIPS[cur.clipIdx]?.src;
    if (src) {
      fetch(src).then(r => r.blob()).then(blob => {
        objectUrl = URL.createObjectURL(blob);
        if (audioRef.current) { audioRef.current.src = objectUrl; audioRef.current.volume = volume; audioRef.current.currentTime = 0; }
      }).catch(() => {});
    }
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cur, CLIPS]);

  // ─── Audio handlers ────────────────────────────────────────────────────────────
  const handleAudioTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    setAudioProgress((el.currentTime / el.duration) * 100);
    const m = Math.floor(el.currentTime / 60);
    const s = Math.floor(el.currentTime % 60).toString().padStart(2, '0');
    setAudioTime(`${m}:${s}`);
    if (el.currentTime >= 30) setHasListenedMinimum(true);
  }, []);
  const handleAudioEnded = useCallback(() => setIsPlaying(false), []);
  const togglePlay = useCallback(() => {
    const el = audioRef.current; if (!el) return;
    if (isPlaying) { el.pause(); setIsPlaying(false); }
    else           { el.play().catch(() => {}); setIsPlaying(true); }
  }, [isPlaying]);

  // ─── STAI / Rating updaters ────────────────────────────────────────────────────
  const updateStai = (phase, id, val) => {
    setAnswers(prev => {
      const m = phase.match(/^(pre|post)_(\d+)$/); if (!m) return prev;
      const blockKey = m[1] === 'pre' ? 'preStai' : 'postStai'; const idx = +m[2];
      const nb = [...prev.blocks]; nb[idx] = { ...nb[idx], [blockKey]: { ...nb[idx][blockKey], [id]: +val } };
      return { ...prev, blocks: nb };
    });
    setErrors(p => { const n = { ...p }; delete n[id]; return n; });
  };
  const updateRating = (clipIdx, id, val) => {
    setAnswers(prev => {
      const nb = [...prev.blocks];
      nb[clipIdx] = { ...nb[clipIdx], clipRating: { ...nb[clipIdx].clipRating, [id]: +val } };
      return { ...prev, blocks: nb };
    });
  };

  const getStaiData = (screen) => screen.id === 'pre_stai'
    ? { phase: `pre_${screen.clipIdx}`,  data: answers.blocks[screen.clipIdx].preStai  }
    : { phase: `post_${screen.clipIdx}`, data: answers.blocks[screen.clipIdx].postStai };

  // ─── Validation ────────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};

    if (cur.id === 'permission' && !answers.permissionGiven)
      errs.permission = 'Please read and accept the consent form to continue.';

    if (cur.id === 'demographics_conceive') {
      const pi = answers.personalInfo;
      if (!pi.name.trim())   errs.name          = 'Name is required.';
      if (!pi.email.trim())  errs.email         = 'Email is required.';
      if (!pi.age)           errs.age           = 'Please select your age group.';
      if (!pi.sex)           errs.sex           = 'Please select your sex.';
      if (!pi.maritalStatus) errs.maritalStatus = 'Please select your marital status.';
      if (!pi.working)       errs.working       = 'Please answer this question.';
      if (!pi.education)     errs.education     = 'Please select your education level.';
      if (!pi.location)      errs.location      = 'Please select your location type.';
      const ci = answers.conceiveInfo;
      if (!ci.tryingToConceive)                                     errs.tryingToConceive = 'Please answer this question.';
      if (ci.tryingToConceive === 'Yes' && !ci.howLong)             errs.howLong          = 'Please select how long.';
      if (ci.tryingToConceive === 'Yes' && !ci.treatment)           errs.treatment        = 'Please answer this question.';
    }

    if (cur.id === 'pss10')
      PSS_ITEMS.forEach(it => { if (answers.pss10[it.id] === null) errs[it.id] = true; });

    if (['pre_stai','clip_stai'].includes(cur.id)) {
      const { data } = getStaiData(cur);
      STAI_ITEMS.forEach(it => { if (data[it.id] === null) errs[it.id] = true; });
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Navigation ────────────────────────────────────────────────────────────────
  const nextScreen = async () => {
    if (!validate()) return;

    // Record listen time before advancing
    if (cur.id === 'listen') {
      setAnswers(prev => {
        const nb = [...prev.blocks];
        nb[cur.clipIdx] = { ...nb[cur.clipIdx], listenedSeconds: audioRef.current?.currentTime || 0 };
        return { ...prev, blocks: nb };
      });
    }

    // ── PSS-10: initial save — personalInfo + conceiveInfo + pss10 ──
    if (cur.id === 'pss10') {
      if (surveyId) { setScreenIdx(p => p + 1); return; } // already saved (resumed)
      setSubmitting(true);
      try {
        const res = await axios.post('/survey/init', {
          personalInfo: answers.personalInfo,
          conceiveInfo: answers.conceiveInfo,
          pss10:        answers.pss10,
        });
        const id = res.data.id;
        setSurveyId(id);
        localStorage.setItem('survey_id', id);
        setScreenIdx(p => p + 1);
      } catch (err) {
        console.error('Init save error:', err);
        setErrors({ _submit: 'Could not save your information. Please check your connection and try again.' });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ── Final save: second-to-last screen — PATCH blocks onto existing doc ──
    if (screenIdx === SCREENS.length - 2) {
      setSubmitting(true);
      const finalBlocks = answers.blocks.map((b, i) => {
        const prevIsNeutral = i > 0 && CLIPS[i - 1].mood === 'neutral';
        return { ...b, preStai: prevIsNeutral ? answers.blocks[i - 1].postStai : b.preStai };
      });
      try {
        await axios.patch(`/survey/${surveyId}`, {
          blocks:    finalBlocks,
          clipOrder: CLIPS.map(c => c.id),
        });
        setScreenIdx(p => p + 1);
      } catch (err) {
        console.error('Final save error:', err);
        setErrors({ _submit: 'There was an error saving your responses. Please try again.' });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (screenIdx < SCREENS.length - 1) setScreenIdx(p => p + 1);
  };

  const prevScreen = () => { if (screenIdx > 0) setScreenIdx(p => p - 1); };

  const nextLabel = () => {
    if (cur.id === 'complete')                      return 'Start New Survey';
    if (cur.id.startsWith('rest'))                  return restCount > 0 ? `Wait (${restCount}s)` : 'Continue';
    if (cur.id === 'listen' && !hasListenedMinimum) return 'Listen for 30s to continue';
    if (cur.id === 'permission')                    return 'I Agree & Continue';
    if (cur.id === 'pss10')                         return 'Save & Continue';
    return 'Continue';
  };

  const nextDisabled =
    (cur.id.startsWith('rest') && restCount > 0) ||
    (cur.id === 'listen' && !hasListenedMinimum) ||
    submitting;

  // ─── Render ────────────────────────────────────────────────────────────────────
  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="survey-shell">
      <span ref={topRef} aria-hidden="true" style={{ position: 'absolute', top: 0 }} />
      <div className="survey-inner">

        {/* Progress */}
        <div className="progress-wrap">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${totalPct}%` }} />
          </div>
          <div className="progress-label">{PHASE_LABEL[cur.id]}</div>
        </div>

        <div className="survey-content animate-in">

          {/* ── PERMISSION ──────────────────────────────────────────────────── */}
          {cur.id === 'permission' && (
            <div>
              <span className="badge badge--teal">Informed Consent</span>
              <h1 className="h1">Before we begin</h1>
              <p className="sub">Please read the following carefully before participating.</p>

              <div className="card consent-card">
                <p className="consent-placeholder">
                  <strong>[Consent Form — Placeholder]</strong><br /><br />
                  Replace this block with the full informed consent text, covering:<br />
                  • Study purpose and background<br />
                  • What participation involves (time, tasks)<br />
                  • Potential risks and benefits<br />
                  • Data confidentiality and storage<br />
                  • Voluntary nature — you may withdraw at any time<br />
                  • Researcher contact details<br /><br />
                  <em>Remove this placeholder before deployment.</em>
                </p>
              </div>

              <div className="card consent-check-card">
                <label className="consent-check-label">
                  <input type="checkbox" className="consent-checkbox"
                    checked={answers.permissionGiven}
                    onChange={e => setAnswers(p => ({ ...p, permissionGiven: e.target.checked }))} />
                  <span>I have read and understood the above information, and I voluntarily agree to participate in this study.</span>
                </label>
                {errors.permission && <p className="error-msg mt-4">{errors.permission}</p>}
              </div>
            </div>
          )}

          {/* ── DEMOGRAPHICS + CONCEIVE ──────────────────────────────────────── */}
          {cur.id === 'demographics_conceive' && (
            <div>
              <span className="badge badge--teal">About You</span>
              <h1 className="h1">Tell us about yourself</h1>

              {/* Personal details */}
              <div className="card mb-4">
                <label className="field-label">Full name <span className="required">*</span></label>
                <input className="text-input" value={answers.personalInfo.name} placeholder="Your name"
                  onChange={e => updatePI('name', e.target.value)} />
                {errors.name && <p className="error-msg">{errors.name}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Email <span className="required">*</span></label>
                <input className="text-input" type="email" value={answers.personalInfo.email} placeholder="you@example.com"
                  onChange={e => updatePI('email', e.target.value)} />
                {errors.email && <p className="error-msg">{errors.email}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Phone number</label>
                <input className="text-input" type="tel" value={answers.personalInfo.phone} placeholder="+91 XXXXX XXXXX"
                  onChange={e => updatePI('phone', e.target.value)} />
              </div>

              <div className="card mb-4">
                <label className="field-label">Age <span className="required">*</span></label>
                <div className="chip-group">
                  {['< 20','20–25','26–30','31–35','36–40','> 40'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.age === o}
                      hasError={!!errors.age} onClick={() => updatePI('age', o)} />
                  ))}
                </div>
                {errors.age && <p className="error-msg">{errors.age}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Sex <span className="required">*</span></label>
                <div className="chip-group">
                  {['Male','Female','Prefer not to say'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.sex === o}
                      hasError={!!errors.sex} onClick={() => updatePI('sex', o)} />
                  ))}
                </div>
                {errors.sex && <p className="error-msg">{errors.sex}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Marital status <span className="required">*</span></label>
                <div className="chip-group">
                  {['Single','Married','Divorced','Widowed'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.maritalStatus === o}
                      hasError={!!errors.maritalStatus} onClick={() => updatePI('maritalStatus', o)} />
                  ))}
                </div>
                {errors.maritalStatus && <p className="error-msg">{errors.maritalStatus}</p>}
              </div>

              {answers.personalInfo.maritalStatus === 'Married' && (
                <div className="card mb-4">
                  <label className="field-label">Duration of marriage</label>
                  <div className="chip-group">
                    {['< 1 year','1–3 years','4–6 years','7–10 years','> 10 years'].map(o => (
                      <Chip key={o} label={o} selected={answers.personalInfo.durationOfMarriage === o}
                        onClick={() => updatePI('durationOfMarriage', o)} />
                    ))}
                  </div>
                </div>
              )}

              <div className="card mb-4">
                <label className="field-label">Number of children</label>
                <div className="chip-group">
                  {['0','1','2','3+'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.numberOfChildren === o}
                      onClick={() => updatePI('numberOfChildren', o)} />
                  ))}
                </div>
              </div>

              {answers.personalInfo.numberOfChildren && answers.personalInfo.numberOfChildren !== '0' && (
                <div className="card mb-4">
                  <label className="field-label">Age of youngest child</label>
                  <div className="chip-group">
                    {['< 1 year','1–3 years','4–6 years','7–12 years','> 12 years'].map(o => (
                      <Chip key={o} label={o} selected={answers.personalInfo.ageOfYoungestChild === o}
                        onClick={() => updatePI('ageOfYoungestChild', o)} />
                    ))}
                  </div>
                </div>
              )}

              <div className="card mb-4">
                <label className="field-label">Employment status <span className="required">*</span></label>
                <div className="chip-group">
                  {['Employed','Self-employed','Unemployed','Homemaker','Student'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.working === o}
                      hasError={!!errors.working} onClick={() => updatePI('working', o)} />
                  ))}
                </div>
                {errors.working && <p className="error-msg">{errors.working}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Highest education level <span className="required">*</span></label>
                <div className="chip-group">
                  {['High school','Undergraduate','Postgraduate','Doctorate','Other'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.education === o}
                      hasError={!!errors.education} onClick={() => updatePI('education', o)} />
                  ))}
                </div>
                {errors.education && <p className="error-msg">{errors.education}</p>}
              </div>

              <div className="card mb-4">
                <label className="field-label">Location type <span className="required">*</span></label>
                <div className="chip-group">
                  {['Urban','Semi-urban','Rural'].map(o => (
                    <Chip key={o} label={o} selected={answers.personalInfo.location === o}
                      hasError={!!errors.location} onClick={() => updatePI('location', o)} />
                  ))}
                </div>
                {errors.location && <p className="error-msg">{errors.location}</p>}
              </div>

              {/* ─ Section divider ─ */}
              <div className="section-divider">
                <span className="section-divider-label">Medical history</span>
              </div>

              <div className="card mb-4">
                <label className="field-label">Are you currently trying to conceive? <span className="required">*</span></label>
                <div className="chip-group">
                  {['Yes','No'].map(o => (
                    <Chip key={o} label={o} selected={answers.conceiveInfo.tryingToConceive === o}
                      hasError={!!errors.tryingToConceive} onClick={() => updateCI('tryingToConceive', o)} />
                  ))}
                </div>
                {errors.tryingToConceive && <p className="error-msg">{errors.tryingToConceive}</p>}
              </div>

              {answers.conceiveInfo.tryingToConceive === 'Yes' && (<>
                <div className="card mb-4">
                  <label className="field-label">How long have you been trying? <span className="required">*</span></label>
                  <div className="chip-group">
                    {['< 6 months','6–12 months','1–2 years','2–3 years','> 3 years'].map(o => (
                      <Chip key={o} label={o} selected={answers.conceiveInfo.howLong === o}
                        hasError={!!errors.howLong} onClick={() => updateCI('howLong', o)} />
                    ))}
                  </div>
                  {errors.howLong && <p className="error-msg">{errors.howLong}</p>}
                </div>

                <div className="card mb-4">
                  <label className="field-label">Are you undergoing any fertility treatment? <span className="required">*</span></label>
                  <div className="chip-group">
                    {['Yes','No'].map(o => (
                      <Chip key={o} label={o} selected={answers.conceiveInfo.treatment === o}
                        hasError={!!errors.treatment} onClick={() => updateCI('treatment', o)} />
                    ))}
                  </div>
                  {errors.treatment && <p className="error-msg">{errors.treatment}</p>}
                </div>

                {answers.conceiveInfo.treatment === 'Yes' && (
                  <div className="card mb-4">
                    <label className="field-label">Type of treatment</label>
                    <div className="chip-group">
                      {['IUI','IVF','Medications','Hormone therapy','Surgical','Other'].map(o => (
                        <Chip key={o} label={o} selected={answers.conceiveInfo.treatmentType === o}
                          onClick={() => updateCI('treatmentType', o)} />
                      ))}
                    </div>
                  </div>
                )}
              </>)}
            </div>
          )}

          {/* ── PSS-10 ──────────────────────────────────────────────────────── */}
          {cur.id === 'pss10' && (() => {
            const anyError = PSS_ITEMS.some(it => errors[it.id]);
            return (
              <div>
                <span className="badge badge--amber">Perceived Stress Scale</span>
                <h1 className="h1">In the last month…</h1>
                <p className="sub">
                  How often have you felt or thought a certain way?{' '}
                  <span className="required">All 10 questions are required.</span>
                </p>
                {anyError && <p className="error-msg mb-4">Please answer all 10 questions before continuing.</p>}
                {errors._submit && <p className="error-msg mb-4">{errors._submit}</p>}

                <div className="card pss-list">
                  {PSS_ITEMS.map((item, idx) => {
                    const hasError = !!errors[item.id];
                    const val = answers.pss10[item.id];
                    return (
                      <div key={item.id} className={`pss-item${idx > 0 ? ' pss-item--border' : ''}`}>
                        <div className={`pss-statement${hasError ? ' pss-statement--error' : ''}`}>
                          <span className="pss-num">{idx + 1}.</span>{' '}
                          How often have you <span className="pss-stem">{item.label}</span>
                        </div>
                        <div className="pss-options">
                          {PSS_OPTIONS.map((opt, i) => (
                            <button key={i} type="button"
                              onClick={() => updatePss(item.id, i)}
                              className={`pss-chip${val === i ? ' pss-chip--selected' : ''}${hasError ? ' pss-chip--error' : ''}`}>
                              <span className="pss-chip-score">{i}</span>
                              <span className="pss-chip-label">{opt}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── STAI ────────────────────────────────────────────────────────── */}
          {['pre_stai','clip_stai'].includes(cur.id) && (() => {
            const { phase, data } = getStaiData(cur);
            const anyError = STAI_ITEMS.some(it => errors[it.id]);
            return (
              <div>
                <h1 className="h1">In this very moment…</h1>
                <p className="sub">Rate each statement from 1 (not at all) to 4 (very much so). <span className="required">All six are required.</span></p>
                {anyError && <p className="error-msg mb-4">Please rate all six statements before continuing.</p>}
                <div className="card stai-list">
                  {STAI_ITEMS.map((item, idx) => {
                    const raw = data[item.id]; const hasError = !!errors[item.id];
                    const score = raw === null ? null : Math.min(4, Math.floor(raw / 25) + 1);
                    const actLabel = score !== null ? SEG_LABELS[score - 1] : null;
                    return (
                      <div key={item.id} className={`stai-item${idx > 0 ? ' stai-item--border' : ''}`}>
                        <div className={`stai-statement${hasError ? ' stai-statement--error' : ''}`}>
                          {item.label}{hasError && <span className="stai-required">(required)</span>}
                        </div>
                        <div className="stai-live-label">
                          {actLabel ? <span className="stai-active-label">{actLabel}</span>
                                    : <span className="stai-placeholder">Drag to respond</span>}
                        </div>
                        <div className="stai-track-wrap">
                          <div className={`stai-track${hasError ? ' stai-track--error' : ''}`} />
                          {raw !== null && <div className="stai-fill" style={{ width: `${raw}%` }} />}
                          {[25,50,75].map(p => <div key={p} className="stai-tick" style={{ left: `${p}%` }} />)}
                          <input type="range" min="0" max="100" step="1" value={raw ?? 50}
                            onChange={e => updateStai(phase, item.id, +e.target.value)} className="stai-input" />
                          {raw !== null && <div className="stai-thumb" style={{ left: `${raw}%` }} />}
                        </div>
                        <div className="stai-seg-labels">
                          {SEG_LABELS.map((lbl, i) => (
                            <span key={i} className={`stai-seg${score === i + 1 ? ' stai-seg--active' : ''}`}
                              style={{ left: `${12.5 + i * 25}%` }}>{lbl}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── CLIP RATING ──────────────────────────────────────────────────── */}
          {cur.id === 'clip_rating' && (
            <div>
              <span className="badge badge--amber">Piece {cur.clipIdx + 1} — Rating</span>
              <h1 className="h1">How was that piece?</h1>
              <p className="sub">Slide each bar to reflect your experience of the music you just heard.</p>
              <div className="card">
                {RATING_ITEMS.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    {idx > 0 && <div className="divider" />}
                    <RatingItem label={item.label} left={item.left} right={item.right}
                      val={answers.blocks[cur.clipIdx].clipRating[item.id]} id={item.id}
                      onChange={(id, val) => updateRating(cur.clipIdx, id, val)} />
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* ── REST ─────────────────────────────────────────────────────────── */}
          {cur.id.startsWith('rest') && (
            <div className="rest-center">
              <span className="badge badge--slate">Rest period</span>
              <h1 className="h1">Take a breath</h1>
              <p className="sub">This short rest lets your mind settle. Sit quietly — eyes closed if comfortable.</p>
              <div className="card rest-card">
                <div className={`rest-counter${restCount > 0 ? ' rest-counter--active pulse' : ''}`}>{restCount}</div>
                <p className="rest-hint">{restCount > 0 ? 'Resting quietly…' : "Whenever you're ready, continue."}</p>
              </div>
            </div>
          )}

          {/* ── LISTEN ───────────────────────────────────────────────────────── */}
          {cur.id === 'listen' && (
            <div>
              <h1 className="h1">Piece {cur.clipIdx + 1}</h1>
              <p className="sub">Find a comfortable position and listen. Close your eyes if you like.</p>
              <div className="card listen-card">
                <div className="listen-icon"><Music size={32} color="var(--teal-400)" /></div>
                <div className="listen-title">Piece {cur.clipIdx + 1}</div>
                <audio ref={audioRef} controlsList="nodownload"
                  onContextMenu={e => e.preventDefault()}
                  onTimeUpdate={handleAudioTimeUpdate} onEnded={handleAudioEnded} />
                <div className="audio-bar">
                  <button className="play-btn" type="button" aria-label={isPlaying ? 'Pause' : 'Play'} onClick={togglePlay}>
                    {isPlaying ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: 2 }} />}
                  </button>
                  <div className="audio-track"><div className="audio-progress" style={{ width: `${audioProgress}%` }} /></div>
                  <span className="audio-time">{audioTime}</span>
                </div>
                <div className="volume-control">
                  <Volume2 size={16} color="var(--text-muted)" />
                  <input type="range" min="0" max="1" step="0.01" value={volume} className="volume-slider"
                    onChange={e => { const v = parseFloat(e.target.value); setVolume(v); if (audioRef.current) audioRef.current.volume = v; }} />
                </div>
                <p className="listen-note">
                  {CLIPS[cur.clipIdx]?.src ? 'Press play when ready.' : 'Add an audio src to CLIPS config, then press play.'}
                </p>
              </div>
            </div>
          )}

          {/* ── COMPLETE ─────────────────────────────────────────────────────── */}
          {cur.id === 'complete' && (
            <div className="complete-center">
              <div className="complete-icon"><CheckCircle2 size={40} color="var(--emerald-400)" /></div>
              <h1 className="h1 text-center">Thank you!</h1>
              <p className="sub text-center">Your responses have been recorded and saved.</p>
              <div className="card summary-card">
                <div className="summary-title">Session Summary</div>
                {CLIPS.map((c, i) => (
                  <div key={i} className="summary-row">
                    <span className="summary-desc">Piece {i + 1}</span>
                    <span className="summary-done"><CheckCircle2 size={14} /> Done</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Bottom nav */}
        {errors._submit && cur.id !== 'pss10' && <p className="error-msg mt-4">{errors._submit}</p>}

        <div className="bottom-nav">
          <div>
            {screenIdx > 0 && cur.id !== 'complete' && (
              <button type="button" onClick={prevScreen} className="back-btn">
                <ArrowLeft size={16} /> Back
              </button>
            )}
          </div>
          {cur.id !== 'complete' && (
            <div className="screen-counter">{screenIdx + 1} / {SCREENS.length}</div>
          )}
          <button type="button"
            onClick={cur.id === 'complete' ? () => window.location.reload() : nextScreen}
            disabled={nextDisabled}
            className={`next-btn${nextDisabled ? ' next-btn--disabled' : ''}`}>
            {submitting ? 'Saving…' : nextLabel()}
            {cur.id !== 'complete' && !submitting && <ArrowRight size={16} />}
          </button>
        </div>

      </div>
    </div>
  );
}
